import React, { useRef, useState } from "react";
import { Offcanvas, Button, Form } from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import DatePicker from "react-multi-date-picker";
import { FaCalendarAlt } from "react-icons/fa";
import axios from "axios";
import AppConstants from "../../AppConstants";
import { Promocode } from "./types";

const promocodeSchema = Yup.object().shape({
  code: Yup.string()
    .required("Promocode is required")
    .min(4, "Promocode must be at least 4 characters")
    .max(10, "Promocode cannot exceed 10 characters"),
  discount: Yup.number()
    .required("Discount is required")
    .min(1, "Discount must be at least 1%")
    .max(100, "Discount cannot exceed 100%"),
  validUntil: Yup.date()
    .required("Expiry date is required")
    .min(new Date(), "Expiry date must be in the future"),
  usageLimitType: Yup.string()
    .required("Please select usage limit type"),
  maxRedemptions: Yup.number()
    .when('usageLimitType', ([usageLimitType], schema) => {
      return usageLimitType === 'restricted'
        ? schema.required("Usage limit is required")
          .min(1, "Usage limit must be at least 1")
          .integer("Usage limit must be a whole number")
        : schema.notRequired();
    }),
  isActive: Yup.boolean()
});

interface CreatePromocodeOffcanvasProps {
  show: boolean;
  handleClose: () => void;
  addPromocode: (promocode: Promocode) => void;
}

const CreatePromocodeOffcanvas: React.FC<CreatePromocodeOffcanvasProps> = ({
  show,
  handleClose,
  addPromocode
}) => {
  const datePickerRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);

  const openDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.openCalendar();
    }
  };


  const createPromocodeAPI = async (promocodeData: any) => {
    try {
      setLoading(true);

      const apiPayload = {
        code: promocodeData.code,
        discount: promocodeData.discount,
        discountType: "percentage",
        validUntil: promocodeData.validUntil ? new Date(promocodeData.validUntil).toISOString() : undefined,
        isActive: promocodeData.isActive,
        createdBy: "60d5ecba3f1b2c001f4b7a2e",
        currency: "usd",
        duration: "once",
        ...(promocodeData.usageLimitType === 'restricted' && {
          maxRedemptions: promocodeData.maxRedemptions
        })
      };

      const response = await axios.post(
        AppConstants.ADMIN_CREATE_PROMOCODE_API,
        apiPayload,
        {
          headers: {
            "Content-Type": "application/json",
          }
        }
      );



      if (response.status === 200 || response.status === 201) {

        const newPromocode: Promocode = {
          id: response.data.id || response.data._id,
          code: response.data.code,
          discount: response.data.discount,
          validUntil: response.data.validUntil
            ? new Date(response.data.validUntil).toLocaleDateString()
            : 'No expiry',
          maxRedemptions: promocodeData.usageLimitType === 'unlimited'
            ? 'Unlimited'
            : (response.data.maxRedemptions || Number(promocodeData.maxRedemptions)),
          usageCount: response.data.usageCount || 0,
          isActive: response.data.isActive
        };


        addPromocode(newPromocode);

        toast.success("Promocode created successfully!", {
          position: "top-right"
        });

        return { success: true, data: response.data };
      }
    } catch (error: any) {
      console.error("Error creating promocode:", error);

      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create promocode. Please try again.";

      toast.error(message, {
        position: "top-right"
      });

      return false;
    } finally {
      setLoading(false);
    }
  };



  return (
    <Offcanvas show={show} onHide={handleClose} placement="end" className="px-3">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="gilroy-semibold text-light" style={{ fontSize: '24px' }}>
          Create Promocode
        </Offcanvas.Title>
      </Offcanvas.Header>
      <hr className="my-0" />
      <Offcanvas.Body>
        <Formik
          initialValues={{
            code: '',
            discount: '',
            validUntil: '',
            usageLimitType: 'restricted',
            maxRedemptions: '',
            isActive: true
          }}
          validationSchema={promocodeSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            const result = await createPromocodeAPI(values);

            if (result && result.success) {
              setSubmitting(false);
              resetForm();
              handleClose();

              // addPromocode(result.data);
            } else {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values, errors, touched, setFieldValue }) => (
            <FormikForm>
              <div className="mb-3">
                <label htmlFor="code" className="form-label">Promocode*</label>
                <Field
                  type="text"
                  name="code"
                  className={`form-control ${errors.code && touched.code ? 'is-invalid' : ''}`}
                  placeholder="Enter Promocode"
                  disabled={loading}
                />
                <ErrorMessage name="code" component="div" className="text-danger" />
              </div>

              <div className="mb-3">
                <label htmlFor="discount" className="form-label">Discount (Percentage)*</label>
                <Field
                  type="number"
                  name="discount"
                  className={`form-control ${errors.discount && touched.discount ? 'is-invalid' : ''}`}
                  placeholder="Enter Discount"
                  disabled={loading}
                  min="1"
                />
                <ErrorMessage name="discount" component="div" className="text-danger" />
              </div>

              <div className="mb-3">
                <label htmlFor="validUntil" className="form-label">Expiry Date*</label>
                <div className="position-relative">
                  <DatePicker
                    ref={datePickerRef}
                    value={values.validUntil}
                    onChange={(date) => {
                      const dateValue = date instanceof Date
                        ? date.toISOString().split('T')[0]
                        : date && date.toString();
                      setFieldValue('validUntil', dateValue);
                    }}
                    minDate={new Date()}
                    format="MM-DD-YYYY"
                    inputClass={`form-control ${errors.validUntil && touched.validUntil ? 'is-invalid' : ''}`}
                    placeholder="Select Expiry Date"
                    disabled={loading}
                  />
                  <div
                    className="position-absolute"
                    style={{ right: '31px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
                    onClick={openDatePicker}
                  >
                    <FaCalendarAlt color="#6c757d" />
                  </div>
                </div>
                <ErrorMessage name="validUntil" component="div" className="text-danger" />
              </div>

              <div className="mb-3">
                <label className="form-label gilroy-semibold">Usage Limit (Users)*</label>
                <div className="d-flex gap-3 mb-2">
                  <Form.Check
                    type="radio"
                    id="unlimited"
                    label="Unlimited"
                    name="usageLimitType"
                    value="unlimited"
                    checked={values.usageLimitType === 'unlimited'}
                    onChange={() => setFieldValue('usageLimitType', 'unlimited')}
                    disabled={loading}
                  />
                  <Form.Check
                    type="radio"
                    id="restricted"
                    label="Restrict"
                    name="usageLimitType"
                    value="restricted"
                    checked={values.usageLimitType === 'restricted'}
                    onChange={() => setFieldValue('usageLimitType', 'restricted')}
                    disabled={loading}
                  />
                </div>
                {values.usageLimitType === 'restricted' && (
                  <>
                    <Field
                      type="number"
                      name="maxRedemptions"
                      className={`form-control ${errors.maxRedemptions && touched.maxRedemptions ? 'is-invalid' : ''}`}
                      placeholder="Enter Number of Users"
                      disabled={loading}
                      min="1"
                    />
                    <ErrorMessage name="maxRedemptions" component="div" className="text-danger" />
                  </>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label d-flex justify-content-between align-items-center">
                  <span className="me-2 gilroy-semibold">Activate/Deactivate</span>
                  <Form.Check
                    type="switch"
                    id="isActive"
                    name="isActive"
                    checked={values.isActive}
                    onChange={(e) => setFieldValue('isActive', e.target.checked)}
                    disabled={loading}
                  />
                </label>
              </div>

              <div className="d-grid gap-2 mt-4">
                <Button
                  variant="success"
                  className="text-white gilroy-semibold font-16"
                  type="submit"
                  disabled={isSubmitting || loading}
                  size="lg"
                >
                  {loading ? "Creating..." : "Create"}
                </Button>
                <Button
                  variant="light"
                  className="text-info bg-transparent gilroy-semibold border-0 font-16"
                  onClick={handleClose}
                  size="lg"
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </FormikForm>
          )}
        </Formik>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default CreatePromocodeOffcanvas;